import React, { useState } from 'react';
import axios from 'axios';
import { useInfiniteQuery } from 'react-query';
import HBox from '@/components/layout/HBox';
import VBox from '@/components/layout/VBox';
import Badge, { BadgeVariants } from '@/components/layout/Badge';
import Botao from '@/components/Botao';
import DataTable, { DataTableHeader } from '@/components/layout/DataTable';
import Icon from '@mdi/react';
import { mdiLoading, mdiFilter, mdiShieldAlertOutline } from '@mdi/js';
import { EventDTO, EventAction } from '@webip/shared/src/dto/firewall';

type ResponseEvents = {
    ok?: boolean;
    result: EventDTO[];
    total: number;
    cursor: string | number | null;
};

const ACTION_VARIANT: Record<EventAction, BadgeVariants> = {
    allow: 'success',
    block: 'danger',
};

export function EventsTab() {
    const [actionFilter, setActionFilter] = useState<EventAction | ''>('');

    const queryEvents = useInfiniteQuery({
        queryKey: ['fw_events', actionFilter],
        queryFn: async ({ pageParam }) => {
            const params = new URLSearchParams({ limit: '50' });
            if (actionFilter) params.append('action', actionFilter);
            if (pageParam) params.append('offset', String(pageParam));
            return await axios.get<ResponseEvents>(`/api/firewall/events?${params}`, {
                withCredentials: true,
            });
        },
        getNextPageParam: lastPage => lastPage.data.cursor ?? undefined,
    });

    const allEvents = queryEvents.data?.pages?.flatMap(p => p.data.result ?? []) ?? [];
    const total = queryEvents.data?.pages?.[0]?.data?.total ?? 0;

    const headers: DataTableHeader[] = [
        {
            label: 'Data/Hora',
            width: '170px',
            render: (row: EventDTO) => {
                const d = new Date(row.time);
                return d.toLocaleDateString('pt-BR') + ' ' + d.toLocaleTimeString('pt-BR');
            },
        },
        {
            label: 'Ação',
            width: '90px',
            alignCenter: true,
            render: (row: EventDTO) => (
                <Badge variant={ACTION_VARIANT[row.action]}>{row.action}</Badge>
            ),
        },
        {
            label: 'Dispositivo',
            column: 'device',
            width: '150px',
        },
        {
            label: 'IP',
            column: 'ip',
            width: '140px',
        },
        {
            label: 'Alvo',
            column: 'target',
            width: '25%',
        },
        {
            label: 'Proto',
            width: '80px',
            render: (row: EventDTO) => row.protocol || '—',
        },
        {
            label: 'Porta',
            width: '70px',
            render: (row: EventDTO) => row.dst_port ? String(row.dst_port) : '—',
        },
        {
            label: 'Categoria',
            width: '120px',
            render: (row: EventDTO) => row.category || '—',
        },
        {
            label: 'Política',
            render: (row: EventDTO) => (
                <span style={{ color: 'var(--color-muted)', fontSize: '0.85em' }}>
                    {row.policy || '—'}
                </span>
            ),
        },
    ];

    return (
        <VBox style={{ gap: '16px', flex: 1, overflow: 'hidden' }}>
            {/* Filtros */}
            <HBox style={{ gap: '8px', alignItems: 'center' }}>
                <Icon path={mdiFilter} size={0.8} />
                <span style={{ fontWeight: 500 }}>Ação:</span>
                {(['', 'allow', 'block'] as const).map(v => (
                    <Botao
                        key={v}
                        size="sm"
                        variant={actionFilter === v ? 'primary' : 'outline-secondary'}
                        onClick={() => setActionFilter(v)}
                    >
                        {v === '' ? 'Todos' : v.toUpperCase()}
                    </Botao>
                ))}

                <span style={{ marginLeft: 'auto', color: 'var(--color-muted)', fontSize: '0.9em' }}>
                    {allEvents.length} / {total} eventos
                </span>
            </HBox>

            {queryEvents.isLoading && (
                <HBox style={{ justifyContent: 'center', padding: '40px' }}>
                    <Icon path={mdiLoading} spin size={2} />
                </HBox>
            )}

            {queryEvents.isError && (
                <p style={{ color: 'var(--color-danger)' }}>Erro ao carregar eventos. Tente novamente.</p>
            )}

            {!queryEvents.isLoading && !queryEvents.isError && allEvents.length === 0 && (
                <VBox style={{ alignItems: 'center', justifyContent: 'center', flex: 1, gap: '12px', color: 'var(--color-muted)', padding: '60px 0' }}>
                    <Icon path={mdiShieldAlertOutline} size={3} />
                    <span style={{ fontSize: '1.1em', fontWeight: 500 }}>Nenhum evento registrado</span>
                    <span style={{ fontSize: '0.9em' }}>
                        Os eventos aparecem aqui quando o agente registrar conexões bloqueadas ou permitidas pelo firewall.
                    </span>
                </VBox>
            )}

            {!queryEvents.isLoading && !queryEvents.isError && allEvents.length > 0 && (
                <DataTable
                    cabecalhos={headers}
                    linhas={allEvents}
                    onLoadMore={() => {
                        if (queryEvents.hasNextPage && !queryEvents.isFetchingNextPage) {
                            queryEvents.fetchNextPage();
                        }
                    }}
                    hasMoreData={queryEvents.hasNextPage}
                    isLoading={queryEvents.isFetchingNextPage}
                />
            )}
        </VBox>
    );
}
